import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useReducer } from "react";
import PlayerNewsHeader from "@/components/playerNews/PlayerNewsHeader";
import PlayerNewsList from "@/components/playerNews/PlayerNewsList";
import RecentStoriesTsx from "@/components/sidebar/RecentStories";
import { ShortFormNewsTypeEnum } from "@/graphql/gql/graphql";
import { FilterOptions, FilterState } from "@/types/playerNews";
import usePlayerUpdates from "@/utils/hooks/usePlayerUpdates";
import logger from "@/utils/logger";
import styles from "./playerNewsContainer.module.scss";

interface PlayerNewsContainerProps {
  title: string;
  sportName: string;
  sportFilters: FilterOptions;
  newsType: ShortFormNewsTypeEnum | null;
}

export type ActionType = {
  type: "SET_FILTER";
  property: string;
  value: string | number | null;
};

export function filterReducer(state: FilterState, action: ActionType) {
  switch (action.type) {
    case "SET_FILTER":
      return {
        ...state,
        [action.property]: action.value,
      };
    default:
      throw new Error();
  }
}

const PlayerNewsContainer = ({
  title,
  sportName,
  sportFilters,
  newsType = null,
}: PlayerNewsContainerProps) => {
  const initialState = {
    newsTypeFilter: newsType,
    teamFilter: null,
    positionFilter: null,
  };
  const [filter, dispatch] = useReducer(filterReducer, initialState);
  const { newsTypeFilter, positionFilter, teamFilter } = filter;
  const params = useSearchParams();
  const team = params ? params.get("team") : null;
  const { updatesData, hasNextPage, getMoreUpdates, isLoading, isFetching } =
    usePlayerUpdates({
      sport: sportName,
      newsTypeFilter,
      positionFilter,
      teamFilter,
    });

  const setFilter = useCallback(
    (property: string, value: string | number | null) => {
      dispatch({
        type: "SET_FILTER",
        property,
        value,
      });
    },
    [dispatch]
  );

  useEffect(() => {
    if (team) {
      try {
        setFilter("teamFilter", parseInt(team, 10));
      } catch {
        logger.error("Invalid teamId", team);
      }
    }
  }, [team, setFilter]);

  const keyForPlayerNewsList = useMemo(() => JSON.stringify(filter), [filter]);

  const handleFilterChange = (
    property: string,
    value: string | number | null
  ) => {
    setFilter(property, value);
  };

  return (
    <>
      <div className={styles.container}>
        <PlayerNewsHeader
          title={title}
          onFilterChange={handleFilterChange}
          sportFilters={sportFilters}
          currentFilter={filter}
          sportName={sportName}
        />
        <div className={styles.contentContainer}>
          <PlayerNewsList
            key={keyForPlayerNewsList}
            news={updatesData}
            showMore={hasNextPage ? getMoreUpdates : undefined}
            loading={isLoading || isFetching}
          />
          <div>
            <RecentStoriesTsx />
          </div>
        </div>
      </div>
    </>
  );
};

export default PlayerNewsContainer;
